import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@component/dialog";
import { Button } from "@component/button";
import { AlertTriangleIcon } from "lucide-react";

interface BYOCDomainPopupProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail?: string;
    companyDomain?: string;
    companyName?: string;
    autoRedirect?: boolean;
}

export default function BYOCDomainPopup({
    isOpen,
    onClose,
    userEmail,
    companyDomain,
    companyName,
    autoRedirect = false,
}: BYOCDomainPopupProps) {
    const [redirectCountdown, setRedirectCountdown] = useState(5);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const detectedCompanyDomain = `${companyDomain}.${import.meta.env.VITE_TALENT_MAIN_DOMAIN}`;

    useEffect(() => {
        if (autoRedirect && detectedCompanyDomain && isOpen) {
            const timer = setInterval(() => {
                setRedirectCountdown((prev) => {
                    if (prev <= 1) {
                        setIsRedirecting(true);

                        window.location.href = `${window.location.protocol}//${detectedCompanyDomain}`;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [autoRedirect, detectedCompanyDomain, isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <div className="flex flex-col items-center text-center space-y-6 py-6">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
                        <AlertTriangleIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                    </div>

                    {/* Title */}
                    <div className="space-y-2">
                        <h2 className="text-xl font-semibold text-foreground">
                            Incorrect Login Portal
                        </h2>
                        <p className="text-sm text-muted-foreground max-w-sm">
                            Your email ({userEmail}) belongs to a different login portal.
                        </p>
                    </div>

                    {/* Auto-redirect countdown */}
                    {autoRedirect && detectedCompanyDomain && !isRedirecting && (
                        <div className="text-sm text-muted-foreground">
                            Redirecting you to {companyName}'s portal in{" "}
                            {redirectCountdown} seconds...
                        </div>
                    )}

                    {isRedirecting && (
                        <div className="text-sm text-muted-foreground">
                            Redirecting to {companyName}'s portal...
                        </div>
                    )}

                    {/* Stop Redirect Button */}
                    {autoRedirect && detectedCompanyDomain && !isRedirecting && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                setRedirectCountdown(0);
                                onClose();
                            }}
                        >
                            Cancel Redirect
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
